import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { setDatePref } from "@/lib/workflow";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authSeq = useRef(0);

  const checkAuth = useCallback(async () => {
    const seq = ++authSeq.current;
    try {
      const { data } = await api.get("/auth/me");
      if (seq !== authSeq.current) return user; // stale response, ignore
      setUser(data);
      setDatePref(data?.date_format);
      return data;
    } catch (_e) {
      if (seq !== authSeq.current) return user; // stale response, ignore
      setUser(null);
      return null;
    } finally {
      if (seq === authSeq.current) setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    authSeq.current += 1; // supersede any in-flight /me
    setUser(data.user);
    setDatePref(data.user?.date_format);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    authSeq.current += 1;
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
